<template>
  <div class="form-field">
    <label v-if="label" :for="id" class="form-label">{{ label }}</label>
    <div class="relative">
      <!-- 前置图标 -->
      <div v-if="prefixIcon" class="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
        <i :class="[prefixIcon, 'text-gray-400']"></i>
      </div>
      
      <!-- 下拉选择框 -->
      <select
        :id="id"
        :name="name || id"
        :value="modelValue"
        @change="$emit('update:modelValue', $event.target.value)"
        :disabled="disabled"
        :class="[
          'form-select',
          prefixIcon ? 'pl-10' : 'pl-4',
          disabled ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : '',
          error ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : '',
          selectClass
        ]"
        v-bind="$attrs"
      >
        <!-- 默认选项 -->
        <option v-if="placeholder" value="" class="text-gray-400">{{ placeholder }}</option>
        
        <!-- 选项 - 简化实现，仅处理常规选项列表 -->
        <option
          v-for="option in normalizedOptions"
          :key="option.value"
          :value="option.value"
          :disabled="option.disabled"
          :class="option.disabled ? 'text-gray-400' : ''"
        >
          {{ option.label }}
        </option>
      </select>
      
      <!-- 后置图标（覆盖默认下拉箭头） -->
      <div class="absolute inset-y-0 right-0 pr-3.5 flex items-center pointer-events-none">
        <i class="fas fa-chevron-down text-gray-400 select-chevron"></i>
      </div>
    </div>
    
    <!-- 帮助文本 -->
    <div v-if="helpText" class="mt-1.5 text-xs text-gray-500">{{ helpText }}</div>
    
    <!-- 错误信息 -->
    <div v-if="error" class="mt-1.5 text-xs text-red-500 flex items-center">
      <i class="fas fa-exclamation-circle mr-1"></i>
      {{ error }}
    </div>
  </div>
</template>

<script setup>
import { defineProps, defineEmits, computed } from 'vue';

const props = defineProps({
  // 选择框标识
  id: {
    type: String,
    required: true
  },
  // 标签文本
  label: {
    type: String,
    default: ''
  },
  // 选择框名称
  name: {
    type: String,
    default: ''
  },
  // 绑定值
  modelValue: {
    type: [String, Number, Boolean],
    default: ''
  },
  // 选项数据
  options: {
    type: Array,
    default: () => []
  },
  // 占位提示文本
  placeholder: {
    type: String,
    default: ''
  },
  // 前缀图标
  prefixIcon: {
    type: String,
    default: ''
  },
  // 禁用状态
  disabled: {
    type: Boolean,
    default: false
  },
  // 帮助文本
  helpText: {
    type: String,
    default: ''
  },
  // 错误信息
  error: {
    type: String,
    default: ''
  },
  // 自定义选择框类名
  selectClass: {
    type: String,
    default: ''
  }
});

defineEmits(['update:modelValue']);

// 规范化选项格式
const normalizedOptions = computed(() => {
  if (!props.options || !Array.isArray(props.options)) {
    return [];
  }
  
  return props.options.map(option => {
    // 如果是简单值（字符串或数字）
    if (typeof option !== 'object' || option === null) {
      return {
        value: option,
        label: option,
        disabled: false
      };
    }
    
    // 如果是对象格式
    return {
      value: option.value !== undefined ? option.value : option.code || '',
      label: option.label || option.name || option.text || option.value || '',
      disabled: option.disabled || false
    };
  });
});
</script>

<style scoped>
.form-field {
  @apply mb-4;
}

.form-label {
  @apply block text-sm font-medium text-gray-700 mb-1.5;
}

.form-select {
  @apply block w-full rounded-md border-gray-300 shadow-sm 
  text-gray-700 placeholder-gray-400 sm:text-sm py-2.5 pr-10
  transition-all duration-200 ease-in-out
  focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500;
  
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background-image: none;
}

/* 设置选项的内边距 */
.form-select option {
  @apply py-1;
}

/* 禁用状态 */
.form-select:disabled {
  @apply opacity-70 cursor-not-allowed;
}

/* 悬停效果 */
.form-select:not(:disabled):hover {
  @apply border-gray-400;
}

/* 下拉图标动画 */
.form-select:focus + div .select-chevron {
  @apply text-blue-500 transform rotate-180;
}

.select-chevron {
  @apply transition-all duration-200 ease-in-out;
}

/* 错误状态 */
.form-select.error {
  @apply border-red-300;
}
.form-select.error:focus {
  @apply ring-red-500/30 border-red-500;
}
</style> 